import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import ewAnalytics from 'utils/analytics';
import { getUserSession } from 'utils/api/sessionApis';
import { addDataToStore } from 'utils/helpers/localStorageHelpers';
import { freshpaint } from 'utils/trackers';
import { UserSession } from 'utils/types/sessionTypes';

import { defaultUser } from './context';

export const useUserContext = () => {
  const { data: sessionResponse, isError } = useQuery<{ data: UserSession }>(
    ['user-session'],
    getUserSession,
    {
      cacheTime: Infinity,
      staleTime: Infinity,
      onSuccess: (response) => {
        addDataToStore(response.data);
        // Removed ldClient.identify call
      },
      onError: () => {
        // Removed ldClient.identify call
      },
    },
  );

  // Check if Freshpaint has any lingering identity
  const hasFreshpaintIdentity = () => {
    const props = window.freshpaint?.cookie?.props;
    return Boolean(props?.$user_id);
  };

  // Analytics tracking
  useEffect(() => {
    const user = sessionResponse?.data;

    if (user?.logged_in === false && hasFreshpaintIdentity()) {
      freshpaint.reset();
    }

    if (user) {
      ewAnalytics.setUserInfo({
        loggedIn: user.logged_in,
        isMember: Boolean(user.is_member),
        id: user.user_id ?? undefined,
      });
    }
  }, [sessionResponse?.data]);

  // Build user data object
  let userData = defaultUser;

  if (sessionResponse?.data) {
    const user = sessionResponse.data;

    userData = {
      loggedIn: user.logged_in,
      isMember: Boolean(user.is_member),
      membershipType: user.membership_type,
      activeCredits: user.active_credits ?? 0,
      userId: user.user_id,
      userEmail: user.user_email,
      nextCreditDate: user.next_credit_date ?? undefined,
      firstName: user.first_name,
      lastName: user.last_name,
      membershipState: user.membership_state ?? undefined,
      fetched: true,
    };
  } else if (isError) {
    userData = { ...defaultUser, fetched: true };
  }

  return {
    userData,
  };
};

export default useUserContext;
